import { ref, onBeforeUnmount } from 'vue'

// Maps the backend cache lifecycle responses (cache_not_ready / cache_missing)
// into an inline AlertNotification for Content Library download failures.
// Decoupled from the views so BcsContentLibrary.vue and the taxonomy items
// view share the exact same UX without duplicating the mapping logic.

const AUTO_DISMISS_MS = 6000

const PREPARING_MESSAGE = 'Preparing your file. It will be ready shortly. Please try again in a moment.'
const UNAVAILABLE_MESSAGE = 'This document is currently unavailable.'

export const useContentLibraryDownloadAlert = () => {
  const downloadAlert = ref({ visible: false, type: 'warning', message: '' })
  let dismissTimerId = null

  const clearDismissTimer = () => {
    if (dismissTimerId) {
      clearTimeout(dismissTimerId)
      dismissTimerId = null
    }
  }

  const dismissDownloadAlert = () => {
    clearDismissTimer()
    downloadAlert.value = { ...downloadAlert.value, visible: false }
  }

  const showDownloadAlert = ({ type, message }) => {
    clearDismissTimer()
    downloadAlert.value = { visible: true, type, message }
    dismissTimerId = setTimeout(() => {
      downloadAlert.value = { ...downloadAlert.value, visible: false }
      dismissTimerId = null
    }, AUTO_DISMISS_MS)
  }

  // Axios with responseType:'arraybuffer' wraps error bodies as ArrayBuffer,
  // so JSON fields are not directly readable. Status code alone is enough
  // to distinguish the cache lifecycle errors we care about here.
  const reportDownloadFailure = (error, { isWhiteLabel = false } = {}) => {
    const statusCode = error?.response?.status
    if (statusCode === 409) {
      showDownloadAlert({ type: 'warning', message: PREPARING_MESSAGE })
      return
    }
    if (statusCode === 404 && isWhiteLabel) {
      showDownloadAlert({ type: 'warning', message: PREPARING_MESSAGE })
      return
    }
    showDownloadAlert({ type: 'error', message: UNAVAILABLE_MESSAGE })
  }

  onBeforeUnmount(() => {
    clearDismissTimer()
  })

  return {
    downloadAlert,
    dismissDownloadAlert,
    reportDownloadFailure
  }
}
